import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dna } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLottery, LOTTERIES, type LotterySlug } from "@/hooks/use-lottery-selection";
import { toast } from "@/hooks/use-toast";

interface GameDnaButtonProps {
  numbers: number[];
  /** Variante visual do botão */
  variant?: "ghost" | "outline";
  /** Tamanho do ícone */
  iconClassName?: string;
  /**
   * Se o jogo é de uma loteria diferente da selecionada no contexto,
   * informe aqui — o botão troca a loteria ativa antes de navegar para o DNA.
   */
  lotterySlug?: LotterySlug;
  /** Texto para o botão "voltar" exibido no DNA. Default: "Voltar" */
  fromLabel?: string;
}

/**
 * Botão que envia um jogo para a página de DNA do jogo,
 * pré-preenchendo as dezenas e disparando a análise automaticamente.
 * Só faz sentido para jogos cuja quantidade de dezenas == config.pick
 * (caso contrário desabilita o botão com tooltip explicativo).
 */
export function GameDnaButton({
  numbers,
  variant = "ghost",
  iconClassName = "h-3.5 w-3.5",
  lotterySlug,
  fromLabel,
}: GameDnaButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { config, setSelected } = useLottery();
  const targetConfig = lotterySlug
    ? LOTTERIES.find((l) => l.slug === lotterySlug) ?? config
    : config;
  const isExact = numbers.length === targetConfig.pick;

  const handleClick = () => {
    if (!isExact) {
      toast({
        title: "Quantidade incompatível",
        description: `O DNA do jogo analisa jogos com exatamente ${targetConfig.pick} dezenas (${targetConfig.name}). Este jogo tem ${numbers.length}.`,
        variant: "destructive",
      });
      return;
    }
    if (lotterySlug && lotterySlug !== config.slug) {
      setSelected(lotterySlug);
    }
    navigate("/app/dna", {
      state: {
        numbers,
        autoAnalyze: true,
        from: location.pathname + location.search,
        fromLabel: fromLabel ?? "Voltar para os jogos",
      },
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant={variant}
            onClick={handleClick}
            disabled={!isExact}
            aria-label="Analisar DNA do jogo"
            className="h-7 w-7 p-0"
          >
            <Dna className={iconClassName} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          {isExact
            ? "Analisar DNA do jogo"
            : `DNA exige ${targetConfig.pick} dezenas`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}